import { type IInstruction } from "@/interfaces/api/instruction.interface"
import { type Moment } from "moment"
import { Project } from "@/models/project.model"
import { Machine } from "@/models/machine.model"
import { Sale } from "@/models/sale.model"
import { Purchase } from "@/models/purchase.model"
import { BaseModelWithFiles } from "@/models/base-with-files.model"
import { RentalIntake } from "@/models/rental-intake.model"
import { RentalDispense } from "@/models/rental-dispense.model"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { Location } from "@/models/location.model"
import { InstructionFeedback } from "@/models/instruction-feedback.model"
import { ModelFactory } from "@/factories/model-factory"
import { normalizeDateTimes } from "@/helper/time-helper"
import type { IExternalHire } from "@/interfaces/api/external-hire.interface"

export class Instruction extends BaseModelWithFiles implements IInstruction {
  public static className = "Instruction" // Used for socket communication.
  public static verboseName = "instructie"
  public static managedResolveFields = [
    "project",
    "planner",
    "location",
    "serviceProvider",
    "machine",
    "contact",
    "preparedBy",
    "reviewedBy"
  ]

  // Since transactions and mutations are lazily loaded, initial state is always an empty array.
  // This means that when receiving socket updates or http responses containing instructions, these fields should be
  // ignored. Otherwise lazily loaded transactions and mutations will be overwritten by empty arrays. //
  public static updateIgnoreFields: string[] = Instruction.extendUpdateIgnoreFields([
    "purchases",
    "sales",
    "rentalIntakes",
    "rentalDispenses",
    "lIsAdministrativeEventsLoadInitiated",
    "lIsAdministrativeEventsLoadCompleted"
  ])

  order: string
  orderReference?: string

  project?: Project
  planner?: KnownBusinessEntityContact
  location?: Location
  serviceProvider?: KnownBusinessEntityContact | null
  machine?: Machine | null
  contact?: KnownBusinessEntityContact | null
  feedback?: InstructionFeedback
  // PoC (PNTS-3198): external party hired through Planmeister that has not confirmed yet.
  externalHire?: IExternalHire | null

  purchases: Purchase[]
  sales: Sale[]
  rentalIntakes: RentalIntake[]
  rentalDispenses: RentalDispense[]

  date: Moment
  timeStart?: Moment | null
  timeEnd?: Moment | null
  plannedDurationHours?: string | null
  body: string
  adminComment: string

  willRecurNextWorkday: boolean
  nextCopy?: string | null // = instruction ID
  nextCopyReference?: string
  nextCopyDate?: Moment | null

  summaryText: string
  flagRental: boolean
  preparedBy?: KnownBusinessEntityContact | null
  preparedAt?: Moment | null
  reviewedBy?: KnownBusinessEntityContact | null
  reviewedAt?: Moment | null
  reference?: string
  lastChangeRelevantToServiceProviderAt?: Moment

  // Local flags
  lIsAdministrativeEventsLoadInitiated: boolean = false
  lIsAdministrativeEventsLoadCompleted: boolean = false

  constructor(instruction: IInstruction, managed = false) {
    super(instruction, managed)

    this.id = instruction.id
    this.order = instruction.order
    this.orderReference = instruction.orderReference
    this.project = ModelFactory.childModel(Project, instruction.project, managed)
    this.planner = ModelFactory.childModel(KnownBusinessEntityContact, instruction.planner, managed)
    this.location = ModelFactory.childModel(Location, instruction.location, managed)
    this.serviceProvider = ModelFactory.childModel(
      KnownBusinessEntityContact,
      instruction.serviceProvider,
      managed
    )
    this.machine = ModelFactory.childModel(Machine, instruction.machine, managed)
    this.contact = ModelFactory.childModel(KnownBusinessEntityContact, instruction.contact, managed)
    this.feedback = ModelFactory.childModel(InstructionFeedback, instruction.feedback, managed)
    this.externalHire = instruction.externalHire ?? null

    this.purchases = ModelFactory.childModels(Purchase, instruction.purchases, managed)
    this.sales = ModelFactory.childModels(Sale, instruction.sales, managed)
    this.rentalIntakes = ModelFactory.childModels(RentalIntake, instruction.rentalIntakes, managed)
    this.rentalDispenses = ModelFactory.childModels(
      RentalDispense,
      instruction.rentalDispenses,
      managed
    )

    this.date = instruction.date
    this.timeEnd = instruction.timeEnd
    this.timeStart = instruction.timeStart
    this.plannedDurationHours = instruction.plannedDurationHours
    this.body = instruction.body ?? ""
    this.adminComment = instruction.adminComment

    this.willRecurNextWorkday = instruction.willRecurNextWorkday
    this.nextCopy = instruction.nextCopy
    this.nextCopyReference = instruction.nextCopyReference
    this.nextCopyDate = instruction.nextCopyDate

    this.summaryText = instruction.summaryText
    this.flagRental = instruction.flagRental
    this.preparedBy = ModelFactory.childModel(
      KnownBusinessEntityContact,
      instruction.preparedBy,
      managed
    )
    this.preparedAt = instruction.preparedAt
    this.reviewedBy = ModelFactory.childModel(
      KnownBusinessEntityContact,
      instruction.reviewedBy,
      managed
    )
    this.reviewedAt = instruction.reviewedAt
    this.reference = instruction.reference
    this.lastChangeRelevantToServiceProviderAt = instruction.lastChangeRelevantToServiceProviderAt
  }

  public normalizeDateTimes() {
    normalizeDateTimes(this.date, this.timeStart, this.timeEnd)
  }

  public hasUnconfirmedChanges() {
    if (this.lastChangeRelevantToServiceProviderAt && !this.feedback?.confirmedAt) {
      // Instruction has changes but never a confirmation.
      return true
    }
    return this.lastChangeRelevantToServiceProviderAt?.isAfter(this.feedback?.confirmedAt)
  }

  public hasAnyAdministrativeItems() {
    return (
      this.purchases.length > 0 ||
      this.sales.length > 0 ||
      this.rentalIntakes.length > 0 ||
      this.rentalDispenses.length > 0
    )
  }

  public isAdministrativeItemsComplete() {
    return (
      this.purchases.every((purchase) => purchase.isComplete()) &&
      this.sales.every((sale) => sale.isComplete()) &&
      this.rentalIntakes.every((rentalIntake) => rentalIntake.isComplete()) &&
      this.rentalDispenses.every((rentalDispense) => rentalDispense.isComplete())
    )
  }

  public isBillable() {
    return !!(this.preparedAt && this.reviewedAt)
  }
}
